declare global {
	interface Element {
		inert: boolean
	}
}

type FocusableElement = Element & Record<'focus', typeof HTMLElement.prototype.focus | undefined> | null;

export class MrMenu extends HTMLElement {
	static get observedAttributes(): Array<string> {
		return [
			'disabled',
			'data-state',
		];
	}

	#keyDownHandler = ( e: KeyboardEvent ): void => {
		if ( this.disabled ) {
			return;
		}

		if ( e.metaKey ) {
			return;
		}

		if ( ( document.activeElement !== this ) && ( !this.contains( document.activeElement ) ) ) {
			return;
		}

		// Close
		if ( e.code === 'Escape' ) {
			this.updateState( 'close' );
			e.preventDefault();
			e.stopPropagation();

			return;
		}

		// Cycle through the list
		if ( e.code === 'ArrowUp' || e.code === 'ArrowDown' ) {
			const menuItems = this.menuItems;
			if ( !menuItems.length ) {
				return;
			}

			e.preventDefault();
			e.stopPropagation();

			const menuItemWithFocus = menuItems.find( ( x ) => {
				return x === document.activeElement;
			} );

			if ( !menuItemWithFocus ) {
				if ( e.code === 'ArrowDown' ) {
					this.firstMenuItem?.focus();
				} else {
					this.lastMenuItem?.focus();
				}

				return;
			}

			const menuItemWithFocusIndex = menuItems.indexOf( menuItemWithFocus );
			let moveFocusTo = menuItemWithFocusIndex;

			if ( e.code === 'ArrowDown' ) {
				moveFocusTo++;
			} else {
				moveFocusTo--;
			}

			if ( moveFocusTo >= menuItems.length ) {
				moveFocusTo = moveFocusTo - menuItems.length;
			}

			menuItems.at( moveFocusTo )?.focus();

			return;
		}

		// Go to item that starts with the same character.
		if ( /^[a-z]$/i.test( e.key ?? '' ) ) {
			const menuItems = this.menuItems;
			if ( !menuItems.length ) {
				return;
			}

			e.preventDefault();
			e.stopPropagation();

			const regex = new RegExp( `^[^a-z]*${e.key}`, 'i' );

			const firstMatch = menuItems.find( ( x ) => {
				return regex.test( x.innerText );
			} );

			firstMatch?.focus();

			return;
		}
	};

	#clickHandler = ( e: Event ): void => {
		if ( this.disabled ) {
			return;
		}

		const target = e.target;
		if ( !target || !( target instanceof HTMLElement ) ) {
			return;
		}

		if ( this.contains( target ) ) {
			return;
		}

		if ( target.closest( `[data-for="${this.id}"` ) ) {
			return;
		}

		this.updateState( 'light-dismiss' );
	};

	#previousActiveElement:FocusableElement = null;

	connectedCallback(): void {
		// Default State
		if ( !this.state ) {
			this.state = 'closed';

			try {
				this.inert = true;
			} catch ( err ) {
				console.warn( err );
			}
		}
	}

	disconnectedCallback(): void {
		window.removeEventListener( 'keydown', this.#keyDownHandler );
		window.removeEventListener( 'click', this.#clickHandler );
	}

	// Attributes
	override setAttribute( attr: string, value: string ): void {
		if ( this.disabled ) {
			return;
		}

		if ( 'data-state' === attr ) {
			const states = [
				'opening',
				'open',
				'closing',
				'closed',
			];

			if ( -1 === states.indexOf( value ) ) {
				return;
			}

			super.setAttribute( 'data-state', value );

			return;
		}

		super.setAttribute( attr, value );
	}

	override removeAttribute( attr: string ): void {
		if ( this.disabled && 'disabled' !== attr ) {
			return;
		}

		super.removeAttribute( attr );
	}

	get disabled(): boolean {
		return this.hasAttribute( 'disabled' );
	}

	set disabled( value: boolean ) {
		if ( value ) {
			this.setAttribute( 'disabled', '' );
		} else {
			this.removeAttribute( 'disabled' );
		}
	}

	get state() :string {
		return this.getAttribute( 'data-state' ) || '';
	}

	set state( value: string ) {
		this.setAttribute( 'data-state', value );
	}

	/**
	 * Update the state of the dialog
	 * @param  {string} directive The directive for the state machine
	 * @return
	 */
	async updateState( d: string ): Promise<void> {
		if ( this.disabled ) {
			return;
		}

		const menuEl = this.menuEl;
		if ( !menuEl ) {
			return;
		}

		let lightDismiss = false;
		let directive = d;
		switch ( this.state ) {
			case 'closed':
				switch ( directive ) {
					case 'toggle-start':
						directive = 'open-start';
						break;
					case 'toggle-end':
						directive = 'open-end';
						break;
				}

				break;
			case 'open':
				switch ( directive ) {
					case 'toggle-start':
					case 'toggle-end':
					case 'light-dismiss':
						lightDismiss = true;
						directive = 'close';
						break;
				}
		}

		switch ( this.state ) {
			case 'closed':
				switch ( directive ) {
					case 'open-start':
					case 'open-end':
						// Store current focus
						if ( document.activeElement && 'focus' in document.activeElement ) {
							this.#previousActiveElement = document.activeElement as FocusableElement;
						}

						// Update focusability
						try {
							this.inert = false;
						} catch ( err ) {
							console.warn( err );
						}

						// Update state
						this.state = 'open';

						if ( directive === 'open-start' ) {
							this.firstMenuItem?.focus();
						} else {
							this.lastMenuItem?.focus();
						}

						// Update toggle buttons
						const buttons = document.querySelectorAll( `[aria-haspopup="true"][aria-controls="${menuEl.id}"]` );
						for ( let i = 0; i < buttons.length; i++ ) {
							buttons[i].setAttribute( 'aria-expanded', 'true' );
						}

						window.addEventListener( 'keydown', this.#keyDownHandler );
						window.addEventListener( 'click', this.#clickHandler, {
							capture: true,
						} );

						break;
					default:
				}
				break;
			case 'open':
				switch ( directive ) {
					case 'close':
						// Update focusability
						try {
							this.inert = true;
						} catch ( err ) {
							console.warn( err );
						}

						// Reset current focus when not a light dismiss action
						if ( !lightDismiss ) {
							this.#previousActiveElement?.focus?.( {
								preventScroll: true,
							} );
						}

						// Update state
						this.state = 'closed';

						// Update toggle buttons
						const buttons = document.querySelectorAll( `[aria-haspopup="true"][aria-controls="${menuEl.id}"]` );
						for ( let i = 0; i < buttons.length; i++ ) {
							buttons[i].setAttribute( 'aria-expanded', 'false' );
						}

						window.removeEventListener( 'keydown', this.#keyDownHandler );
						window.removeEventListener( 'click', this.#clickHandler );

						break;
					default:
				}
				break;
			default:
		}
	}

	get menuEl(): HTMLElement | null {
		return this.querySelector( 'ul[role="menu"]' );
	}

	get menuItems(): Array<HTMLElement> {
		const menuEl = this.menuEl;
		if ( !menuEl ) {
			return [];
		}

		return Array.from( this.querySelectorAll<HTMLElement>( `
			ul[role="menu"] > li[role="menuitem"],
			ul[role="menu"] > li [role="menuitem"]
		` ) ).filter( ( x ) => {
			// Filter out menu items from nested menus
			return x.closest( 'ul[role="menu"]' ) === menuEl;
		} );
	}

	get firstMenuItem(): HTMLElement | null {
		return this.menuItems.at( 0 ) ?? null;
	}

	get lastMenuItem(): HTMLElement | null {
		return this.menuItems.at( -1 ) ?? null;
	}
}
